import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider } from './contexts/AuthContext';
import { OfflineProvider } from './context/OfflineContext';
import OfflineIndicator from './components/OfflineIndicator';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import { CalculatorWrapper } from './components/Calculator';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Clients from './pages/Clients';
import Products from './pages/Products';
import Reports from './pages/Reports';
import DeliveryNotes from './pages/DeliveryNotes';
import DeliveryNoteDetail from './pages/DeliveryNoteDetail';
import StockShortages from './pages/StockShortages';
import HistoricalOrders from './pages/HistoricalOrders';
import Users from './pages/Users';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OfflineProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />

            {/* Private routes */}
            <Route
              path="/calculator"
              element={
                <PrivateRoute>
                  <CalculatorWrapper />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/orders"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <Orders />
                  </Layout>
                </PrivateRoute>
              }
            />
            
            <Route
              path="/orders/:id"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <OrderDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            
            {/* Admin only routes */}
            <Route
              path="/clients"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <Clients />
                  </Layout>
                </PrivateRoute>
              }
            />
            
            <Route
              path="/products"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <Products />
                  </Layout>
                </PrivateRoute>
              }
            />
            
            <Route
              path="/reports"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <Reports />
                  </Layout>
                </PrivateRoute>
              }
            />
            
            <Route
              path="/delivery-notes"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <DeliveryNotes />
                  </Layout>
                </PrivateRoute>
              }
            />
            
            <Route
              path="/delivery-notes/:id"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <DeliveryNoteDetail />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/stock-shortages"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <StockShortages />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/historico"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <HistoricalOrders />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/users"
              element={
                <PrivateRoute adminOnly>
                  <Layout>
                    <Users />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
          <OfflineIndicator />
        </Router>
        </OfflineProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;